import { FirebaseMessaging } from "@capacitor-firebase/messaging";
import { Capacitor } from "@capacitor/core";
import { Device } from "@capacitor/device";
import { handlePushNotificationActions } from "@/features/push_notifications/handlePushNotificationActions";
import posthog from "posthog-js";
import { trpcClient } from "@/trpc";
import { Storage } from "@/common/utils/storage";
import { apiRequestPostWithRetry } from "@/ajax/utils/apiRequest";
import { router } from "@/router";
import { queryClient } from "@/ajax/queryClient";
import { isImpersonating } from "@/features/impersonation/impersonation-session";

type NotificationPermissionState = "granted" | "denied" | "prompt";

export const PUSH_PERMISSION_STATUS_QUERY_KEY = ["pushPermissionStatus"];

// Define the push notification action types to match handlePushNotificationActions
type PushNotificationActionType =
  | "CREDENTIAL_EXPIRATION_REMINDER"
  | "CHAT_NOTIFICATION"
  | "JOB_POSTS_NOTIFICATION"
  | "CHAT_NOTIFICATION_ORG"
  | "JOB_POST_INTEREST"
  | "TIME_ENTRY_REMINDER"
  | "TIMESHEET_UPDATE"
  | "JOB_OFFER_EDIT_PROPOSAL_ACCEPTED"
  | "JOB_OFFER_EDIT_PROPOSAL_ACCEPTED_ORG"
  | "JOB_OFFER_ACCEPTED_ORG"
  | "JOB_OFFER_REJECTED_ORG"
  | "JOB_OFFER_NEW"
  | "HIRING_PROCESS_PENDING"
  | "FORM_ASSIGNMENT_COMPLETED_ORG"
  | "FORM_ASSIGNMENT_REJECTED_ORG"
  | "FORM_ASSIGNMENT_RECEIVED"
  | "TIMESHEET_SUBMITTED_ORG";

type PushNotificationData =
  | { id: string }
  | { id: string }
  | Record<never, never>
  | { id: string; organizationId: string; organizationBusinessUnitId: string }
  | {
      jobPostId: string;
      organizationId?: string;
      organizationBusinessUnitId?: string;
    }
  | { jobOfferId: string }
  | {
      jobOfferId: string;
      organizationId?: string;
      organizationBusinessUnitId: string;
    }
  | {
      formAssignmentId: string;
      organizationBusinessUnitId: string;
      chatThreadId?: string;
    };

export class PushNotificationService {
  private static readonly LAST_PERMISSION_CHECK_KEY =
    "push_permission_last_check";
  // Check if we're running on a native platform that supports push
  private static isPushAvailable(): boolean {
    return (
      Capacitor.isNativePlatform() &&
      (Capacitor.getPlatform() === "ios" ||
        Capacitor.getPlatform() === "android")
    );
  }

  // Request permission safely
  static async requestPermission(): Promise<boolean> {
    if (!this.isPushAvailable()) {
      console.error("Push notifications are not available on this platform");
      throw new Error("Push notifications are not available on this platform");
    }

    const permissionState = await this.getNotificationPermissionState();

    if (permissionState === "granted") {
      posthog.capture("push_notification.granted");
      await this.registerDeviceToken();
      await queryClient.invalidateQueries({
        queryKey: PUSH_PERMISSION_STATUS_QUERY_KEY,
      });
      return true;
    }

    if (permissionState === "prompt") {
      posthog.capture("push_notification.request_permission");
    }

    const result = await FirebaseMessaging.requestPermissions();
    const accepted = result.receive === "granted";

    if (!accepted) {
      return false;
    }

    posthog.capture("push_notification.granted");
    await this.registerDeviceToken();
    await queryClient.invalidateQueries({
      queryKey: PUSH_PERMISSION_STATUS_QUERY_KEY,
    });
    return true;
  }

  static setupPushListeners(): void {
    if (!this.isPushAvailable()) {
      return;
    }

    // FCM listeners
    FirebaseMessaging.addListener(
      "notificationReceived",
      (notificationEvent) => {
        const { notification } = notificationEvent;
        console.log("notification", notification);
        posthog.capture("push_notification.received", {
          notificationId: notification.id,
          title: notification.title,
          data: notification.data,
        });
      },
    );

    // Push action performed
    FirebaseMessaging.addListener(
      "notificationActionPerformed",
      async (action) => {
        const notificationData = action.notification.data as
          | Record<string, unknown>
          | undefined;
        const actionType = notificationData?._action as string | undefined;
        const notificationId = notificationData?._notificationId as
          | string
          | undefined;

        posthog.capture("push_notification.clicked", {
          action: actionType,
          title: action.notification.title,
          body: action.notification.body,
        });

        // Report notification opened to backend before navigating
        // (this won't throw - errors are caught and logged internally)
        if (notificationId) {
          await reportNotificationOpened(notificationId);
        }

        if (
          actionType &&
          notificationData &&
          isPushNotificationActionType(actionType)
        ) {
          handlePushNotificationActions({
            action: actionType,
            data: notificationData as PushNotificationData,
          });
          return;
        }

        const notificationUrl = getNotificationUrl(notificationData);
        if (notificationUrl) {
          navigateToPushUrl(notificationUrl);
          return;
        }

        if (actionType) {
          console.error("Invalid push notification action type:", actionType);
        } else {
          console.error("Missing push notification action or URL");
        }
      },
    );
  }

  // Public method to check current permission status
  static async checkPermissionStatus() {
    if (!this.isPushAvailable()) {
      return null;
    }
    const permissionState = await this.getNotificationPermissionState();
    return { receive: permissionState };
  }

  private static async getNotificationPermissionState(): Promise<NotificationPermissionState> {
    const result = await FirebaseMessaging.checkPermissions();
    switch (result.receive) {
      case "granted":
        return "granted";
      case "denied":
        return "denied";
      default:
        return "prompt";
    }
  }

  public static async registerDeviceToken() {
    // Never route the impersonated user's notifications to a staff device.
    if (isImpersonating()) {
      return;
    }
    const tokenResult = await FirebaseMessaging.getToken();
    const [deviceInfo, deviceId] = await Promise.all([
      Device.getInfo(),
      Device.getId(),
    ]);

    if (!["ios", "android"].includes(deviceInfo.platform)) {
      return;
    }

    const platformId = deviceInfo.platform === "ios" ? 1 : 2;

    await trpcClient.user.updateDeviceToken.mutate({
      deviceToken: tokenResult.token,
      platformId,
      platformVersion: deviceInfo.osVersion ?? null,
      deviceId: deviceId.identifier,
    });
  }

  public static async removeDeviceToken() {
    const [deviceInfo, deviceId] = await Promise.all([
      Device.getInfo(),
      Device.getId(),
    ]);

    if (!["ios", "android"].includes(deviceInfo.platform)) {
      return;
    }

    const platformId = deviceInfo.platform === "ios" ? 1 : 2;

    await trpcClient.user.deleteDeviceToken.mutate({
      deviceId: deviceId.identifier,
      platformId,
    });
  }

  static async checkPermissionOncePerDay() {
    if (!this.isPushAvailable()) {
      return;
    }
    const today = new Date().toISOString().split("T")[0];
    const lastCheck = await Storage.getItem(this.LAST_PERMISSION_CHECK_KEY);
    if (lastCheck === today) {
      return;
    }
    await this.checkPermissionStatus();
    await Storage.setItem(this.LAST_PERMISSION_CHECK_KEY, today);
  }
}

// Type guard function to check if the action is a valid push notification action type
function isPushNotificationActionType(
  action: string,
): action is PushNotificationActionType {
  return [
    "CREDENTIAL_EXPIRATION_REMINDER",
    "CHAT_NOTIFICATION",
    "JOB_POSTS_NOTIFICATION",
    "CHAT_NOTIFICATION_ORG",
    "JOB_POST_INTEREST",
    "TIME_ENTRY_REMINDER",
    "TIMESHEET_UPDATE",
    "JOB_OFFER_EDIT_PROPOSAL_ACCEPTED",
    "JOB_OFFER_EDIT_PROPOSAL_ACCEPTED_ORG",
    "JOB_OFFER_ACCEPTED_ORG",
    "JOB_OFFER_REJECTED_ORG",
    "JOB_OFFER_NEW",
    "HIRING_PROCESS_PENDING",
    "FORM_ASSIGNMENT_COMPLETED_ORG",
    "FORM_ASSIGNMENT_REJECTED_ORG",
    "FORM_ASSIGNMENT_RECEIVED",
    "TIMESHEET_SUBMITTED_ORG",
  ].includes(action);
}

function getNotificationUrl(
  notificationData: Record<string, unknown> | undefined,
): string | null {
  if (!notificationData) {
    return null;
  }

  for (const key of ["url", "link", "actionLink", "action_link"]) {
    const value = notificationData[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  return null;
}

function navigateToPushUrl(url: string): void {
  const navigationTarget = getInternalNavigationTarget(url);
  if (!navigationTarget) {
    console.error("Invalid push notification URL:", url);
    return;
  }

  void router.navigate(
    navigationTarget as Parameters<typeof router.navigate>[0],
  );
}

function getInternalNavigationTarget(
  url: string,
): { to: string; search?: Record<string, string> } | null {
  try {
    const parsedUrl = new URL(url, window.location.origin);
    const isRelativeUrl = url.startsWith("/");
    const isHttpUrl =
      parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";
    const isSaileUrl =
      parsedUrl.hostname === window.location.hostname ||
      parsedUrl.hostname.endsWith("saileapp.com");

    if (isHttpUrl && !isRelativeUrl && !isSaileUrl) {
      return null;
    }

    const to =
      isHttpUrl || isRelativeUrl
        ? parsedUrl.pathname
        : `/${parsedUrl.hostname}${parsedUrl.pathname}`;
    const search = Object.fromEntries(parsedUrl.searchParams.entries());

    return Object.keys(search).length > 0 ? { to, search } : { to };
  } catch {
    return null;
  }
}

/**
 * Report to the backend that a notification was opened/clicked.
 * This updates the openedAt, openedCount, and lastOpenedAt fields.
 */
async function reportNotificationOpened(notificationId: string): Promise<void> {
  try {
    await apiRequestPostWithRetry({
      path: "/communication/push/opened",
      body: { notificationId },
      retryOptions: { maxRetries: 2 },
    });
  } catch (error) {
    // Log but don't throw - we don't want to block the user's navigation
    console.error("Failed to report notification opened:", error);
  }
}
